import React, { useEffect, useState, useRef } from 'react'

import { MdOutlineExpandLess } from 'react-icons/md'
import { MdOutlineExpandMore } from 'react-icons/md'
import { callAPI } from '../hooks/api'
import { toast } from 'react-toastify'
import Spinner from './Spinner'

export default function AdminCardView({
  sessionKey,
  panelName,
  data,
  setData,
  logout,
}) {
  const [loading, setLoading] = useState(false)
  const [columnNames, setColumNames] = useState({})
  const [valueChanged, setValueChanged] = useState(false)

  const handleRatingChange = (event, index, type, columnName) => {
    const newData = [...data[type]]

    // Convert the input value to an integer
    let value = parseInt(event.target.value, 10)

    // Check if the input is not a number (NaN) or is outside the -1 to 10 range and adjust accordingly
    if (isNaN(value)) {
      value = '' // or you could set a default like 0
    } else {
      value = Math.max(-1, Math.min(value, 10)) // Ensure the value is between -1 and 10
    }

    // Update the specific item's rating (or other property if needed)
    newData[index][columnName] = value.toString()
    newData[index].changed = 1

    // Set the updated data back to state
    setData({ ...data, [type]: newData })
    setValueChanged(true) // Assuming you are tracking if the data has changed
  }

  const handleTextChange = (event, index, type, columnName) => {
    const newData = [...data[type]]

    // Update the specific item's rating (or other property if needed)
    newData[index][columnName] = event.target.value
    newData[index].changed = 1

    // Set the updated data back to state
    setData({ ...data, [type]: newData })
    setValueChanged(true) // Assuming you are tracking if the data has changed
  }
  const getTableName = (panelName) => {
    if (panelName === 'executiveSubbasin') {
      return 'executive_summary_subbasin'
    } else if (panelName === 'executiveGSA') {
      return 'executive_summary_gsa'
    } else if (panelName === 'gwRating') {
      return 'pro_gw_ratings'
    } else if (panelName === 'wdRating') {
      return 'pro_wd_ratings'
    }
  }

  const updateDatabase = async () => {
    let tableName = getTableName(panelName)

    var endpoint = '/update_rating'
    var params = {
      table_name: tableName,
      data: JSON.stringify(
        data[panelName].filter((item) => item.changed === 1)
      ),
      session_key: sessionKey,
    }
    setLoading(true)
    var res = await callAPI(endpoint, params)
    setLoading(false)

    if (res.status === 'expire') {
      toast.warn(res.msg)
      logout()
    } else if (res.status === 'success') {
      let currentData = data[panelName]
      let preparedData = currentData.map((item) => ({ ...item, changed: 0 }))
      setData((prevState) => {
        // Update the specific panel data to reflect that changes have been processed
        return {
          ...prevState,
          [panelName]: preparedData,
        }
      })
      setValueChanged(false)
      toast.success('Successffully Updated!')
    }
  }

  const getPanelTitle = (panelName) => {
    if (panelName === 'executiveSubbasin') {
      return 'Executive Subbasin'
    } else if (panelName === 'executiveGSA') {
      return 'Executive GSA'
    } else if (panelName === 'gwRating') {
      return 'Groundwater Rating'
    } else if (panelName === 'wdRating') {
      return 'Water District Rating'
    }
  }

  useEffect(() => {
    if (panelName === 'executiveSubbasin') {
      setColumNames({
        subbasin_no: 'Subbasin No.',
        subbasin_name: 'subbasin Name',
        subbasin_status_update: 'Subbasin Status Update',
        executive_summary: 'Executive Summary',
      })
    } else if (panelName === 'executiveGSA') {
      setColumNames({
        subbasin_no: 'Subbasin No.',
        gsa_name: 'GSA Name',
        allocation: 'Allocation',
        allocation_status_update: 'Allocation Status Update',
      })
    } else if (panelName === 'gwRating') {
      setColumNames({ gsa_name: 'GSA Name', rating: 'Rating' })
    } else if (panelName === 'wdRating') {
      setColumNames({ agencyname: 'Agency Name', rating: 'Rating' })
    }
  }, [])

  if (loading) {
    return <Spinner local={true} />
  }
  return (
    <>
      <div className="w-full flex items-center justify-between text-black text-m font-medium rounded transition duration-150 ease-in-out mt-2 mb-2 p-6">
        <div>{getPanelTitle(panelName)}</div>

        <div className="flex space-x-4">
          <button
            onClick={updateDatabase}
            className={`${valueChanged ? 'bg-[#007377] hover:bg-[#01589d] focus:bg-[#01589d] cursor-pointer' : 'bg-gray-600 hover:bg-gray-700 focus:bg-gray-700 cursor-not-allowed'} text-white font-medium text-sm rounded shadow-md hover:shadow-lg focus:shadow-lg w-[270px] text-center transition duration-150 ease-in-out`}
            disabled={!valueChanged}
          >
            Update {getPanelTitle(panelName)}
          </button>
        </div>
      </div>

      <>
        <div
          key={panelName}
          className="min-h-[100px] max-h-[700px] justify-between items-center m-[10px] overflow-x-auto"
        >
          <div className="relative">
            <form>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  padding: '10px',
                }}
              >
                <thead>
                  <tr>
                    {Object.entries(columnNames).map(([key, val], index) => (
                      <th
                        key={index}
                        style={{
                          textAlign: 'center',
                          fontWeight: 'bold',
                          width: `${100 / Object.keys(columnNames).length}%`,
                        }}
                      >
                        {val}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data[panelName].map((item, index) => (
                    <tr key={index}>
                      {Object.entries(columnNames).map(([key, val]) => (
                        <td
                          key={key}
                          style={{
                            textAlign: 'center',
                            width: `${100 / Object.keys(columnNames).length}%`,
                          }}
                        >
                          {key === 'rating' ? (
                            <input
                              type="number"
                              min={-1}
                              max={10}
                              style={{
                                textAlign: 'center',
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                maxHeight: '100px',
                              }}
                              value={item[key]}
                              onChange={(e) =>
                                handleRatingChange(e, index, panelName, key)
                              }
                            />
                          ) : key === 'allocation_status_update' ||
                            key === 'executive_summary' ||
                            key === 'subbasin_status_update' ? (
                            <textarea
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                minHeight: '100px', // Set a minimum height to make it visually apparent that it can hold multiple lines
                                maxHeight: '200px', // Optional: Set a maximum height if needed
                                overflowY: 'auto', // Allows scrolling within the textarea
                              }}
                              value={item[key]}
                              onChange={(e) =>
                                handleTextChange(e, index, panelName, key)
                              }
                            />
                          ) : key === 'allocation' ? (
                            <input
                              type="text"
                              style={{
                                textAlign: 'center',
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                maxHeight: '100px',
                              }}
                              value={item[key]}
                              onChange={(e) =>
                                handleTextChange(e, index, panelName, key)
                              }
                            />
                          ) : (
                            <div style={{ textAlign: 'center' }}>
                              {item[key]}
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </form>
          </div>
        </div>
      </>
    </>
  )
}
